import React, { useState, useRef, useEffect } from "react";

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  onChange?: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function Dropdown({
  value,
  onChange,
  options,
  placeholder = "선택하세요",
  disabled = false,
  className = "",
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // 선택된 옵션의 label 찾기
  const selectedLabel = options.find((opt) => opt.value === value)?.label || placeholder;

  // 외부 클릭 감지 및 스크롤 처리
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  return (
    <div
      className={`custom-dropdown ${className}`}
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* 드롭다운 버튼 */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "4px 8px",
          fontSize: "13px",
          fontWeight: "500",
          color: "#24292f",
          background: isOpen ? "#eaeef2" : "#f6f8fa",
          border: `1px solid ${isOpen ? "#0969da" : "#d0d7de"}`,
          borderRadius: "6px",
          outline: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "center",
          boxSizing: "border-box",
          transition: "all 0.15s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          position: "relative",
          paddingRight: "28px",
          boxShadow: isOpen ? "0 0 0 3px rgba(9, 105, 218, 0.1)" : "none",
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.background = "#eaeef2";
            e.currentTarget.style.borderColor = "#0969da";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.background = "#f6f8fa";
            e.currentTarget.style.borderColor = "#d0d7de";
          }
        }}
      >
        <span style={{ flex: 1 }}>{selectedLabel}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          style={{
            position: "absolute",
            right: "8px",
            transition: "transform 0.15s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <path fill="#586069" d="M6 9L1 4h10z" />
        </svg>
      </button>

      {/* 드롭다운 목록 */}
      {isOpen && dropdownPosition && (
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            background: "#ffffff",
            border: "1px solid #d0d7de",
            borderRadius: "6px",
            boxShadow: "0 8px 24px rgba(140, 149, 159, 0.2)",
            zIndex: 10000,
            maxHeight: "240px",
            overflowY: "auto",
            boxSizing: "border-box",
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              style={{
                padding: "8px 12px",
                fontSize: "13px",
                color: "#24292f",
                textAlign: "center",
                cursor: "pointer",
                transition: "background 0.1s ease",
                background: option.value === value ? "#f6f8fa" : "#ffffff",
                fontWeight: option.value === value ? "600" : "normal",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f2ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  option.value === value ? "#f6f8fa" : "#ffffff";
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
